import { createContext, useContext, useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { onAuthStateChanged } from "firebase/auth";
import { getApp } from "firebase/app";
import { db, auth } from "../config/firebase";

const BillingContext = createContext({
  plan: "free",
  subscriptionStatus: null,
  subscriptionInterval: null,
  periodEnd: null,
  billingLoading: true,
  startCheckout: async () => {},
  openPortal: async () => {},
  isPro: false,
  isTrialing: false,
  trialDaysLeft: 0,
});

export const useBilling = () => useContext(BillingContext);

const fns = getFunctions(getApp(), "us-central1");
const createCheckoutSession = httpsCallable(fns, "createCheckoutSession");
const createPortalSession   = httpsCallable(fns, "createPortalSession");

export function BillingProvider({ children }) {
  const [uid, setUid]                                    = useState(null);
  const [plan, setPlan]                                  = useState("free");
  const [subscriptionStatus, setSubscriptionStatus]      = useState(null);
  const [subscriptionInterval, setSubscriptionInterval]  = useState(null);
  const [periodEnd, setPeriodEnd]                        = useState(null);
  const [billingLoading, setBillingLoading]              = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUid(u?.uid ?? null));
    return unsub;
  }, []);

  useEffect(() => {
    if (!uid) { setBillingLoading(false); return; }

    const unsub = onSnapshot(doc(db, "users", uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setPlan(data.plan ?? "free");
        setSubscriptionStatus(data.subscriptionStatus ?? null);
        setSubscriptionInterval(data.subscriptionInterval ?? null);
        setPeriodEnd(data.subscriptionCurrentPeriodEnd ?? null);
      }
      setBillingLoading(false);
    });
    return unsub;
  }, [uid]);

  const startCheckout = async (interval) => {
    try {
      const { data } = await createCheckoutSession({ interval });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No URL returned from checkout session");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      throw err;
    }
  };

  const openPortal = async () => {
    const { data } = await createPortalSession();
    window.location.href = data.url;
  };

  const isTrialing   = plan === "pro" && subscriptionStatus === "trialing";
  const isPro        = plan === "pro" && (subscriptionStatus === "active" || subscriptionStatus === "trialing");
  const trialDaysLeft = isTrialing && periodEnd
    ? Math.max(0, Math.ceil((new Date(periodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <BillingContext.Provider value={{
      plan, subscriptionStatus, subscriptionInterval, periodEnd,
      billingLoading, startCheckout, openPortal,
      isPro, isTrialing, trialDaysLeft,
    }}>
      {children}
    </BillingContext.Provider>
  );
}
